import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import EmployerPerformanceOptions from "./EmployerPerformanceOptions";
import AgencyPerformanceOptions from "./AgencyPerformanceOptions";
import EmployerPerformancePdfExportPage from "./EmployerPerformancePdfExportPage";
import AgencyPerformancePdfExportPage from "./AgencyPerformancePdfExportPage";
import Button from "../../shared/button/Button";
import pdfExportOptionsTypes from "../types/pdfExportOptionsTypes";
import {
  Overlay,
  ModalWrapper,
  Title,
  TitleText,
  CloseButton,
  ModalBody,
  Options,
  Preview,
  SectionTitle,
  ButtonsWrapper,
} from "../styles/export-pdf-modal-styles";

interface IOptionItemProps {
  id: number;
  value: string;
  isChecked: boolean;
  label: string;
}

export interface IExportModalProps {
  toggleModal: () => void;
  isPortalUser: boolean;
  options: Array<IOptionItemProps>;
  tabSelected: string;
}

export const ExportPdfModal: FunctionComponent<IExportModalProps> = ({
  toggleModal,
  isPortalUser,
  options,
  tabSelected,
}) => {
  const createPdf = (): void => {
    const divToDisplay = document.getElementById("pdf-export-page");
    const userType = tabSelected;
    const brand = isPortalUser ? "Portal" : "Hiring Hub";
    const date: Date = new Date();
    const day = date.getDate();
    const month = date.toLocaleString("default", {
      month: "long",
    });
    const year = date.getFullYear();
    const dateToDisplay = `${month} ${day}, ${year}`;
    const pdfName = `${brand} Insights - ${userType} performance - ${dateToDisplay}.pdf`;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    html2canvas(divToDisplay).then((canvas) => {
      const divImage = canvas.toDataURL("image/jpeg");
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        unit: "px",
      });
      pdf.addImage(divImage, "JPEG", 0, 0, 447, 632, "FAST");
      pdf.save(pdfName, { returnPromise: true });
    });
  };

  const isButtonDisabled = () => {
    const selectedOptions = [];
    options.map((option) => {
      if (option.isChecked) {
        selectedOptions.push(option);
      }
      return true;
    });
    if (selectedOptions.length > 0) {
      return false;
    }
    return true;
  };

  return (
    <Overlay>
      <ModalWrapper className="animated delay fadeInDown">
        <Title>
          <TitleText>Export to PDF</TitleText>
          <CloseButton onClick={() => toggleModal()}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </Title>
        <ModalBody>
          <Options>
            <SectionTitle>Options</SectionTitle>
            {tabSelected === "employer" && <EmployerPerformanceOptions />}
            {tabSelected === "agency" && <AgencyPerformanceOptions />}
          </Options>
          <Preview>
            <SectionTitle>Preview</SectionTitle>
            {tabSelected === "employer" && (
              <EmployerPerformancePdfExportPage
                preview
                noneSelected={isButtonDisabled()}
              />
            )}
            {tabSelected === "agency" && (
              <AgencyPerformancePdfExportPage
                preview
                noneSelected={isButtonDisabled()}
              />
            )}
          </Preview>
        </ModalBody>
        <ButtonsWrapper>
          <Button secondary buttonName="Cancel" onClickAction={toggleModal} />
          <Button
            primary
            buttonName="Export PDF"
            onClickAction={createPdf}
            disabled={isButtonDisabled()}
          />
        </ButtonsWrapper>
      </ModalWrapper>
      {tabSelected === "employer" && <EmployerPerformancePdfExportPage />}
      {tabSelected === "agency" && <AgencyPerformancePdfExportPage />}
    </Overlay>
  );
};

ExportPdfModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  isPortalUser: PropTypes.bool.isRequired,
  options: pdfExportOptionsTypes.isRequired,
  tabSelected: PropTypes.string.isRequired,
};

interface IReduxState {
  employerAnalytics: {
    isPortalUser: boolean;
    pdfExportOptions: {
      options: Array<IOptionItemProps>;
    };
    tabSelected: string;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  isPortalUser: state.employerAnalytics.isPortalUser,
  options: state.employerAnalytics.pdfExportOptions.options,
  tabSelected: state.employerAnalytics.tabSelected,
});

export default connect(mapStateToProps, {})(ExportPdfModal);
